/**
 * Convert a PDF file to an array of page image data URLs (client-side only).
 * Used so PreTrip flip books can be created from uploaded PDFs without a backend.
 */

import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist'

/** Render scale: keep moderate so JPEG page blobs stay under platform request limits (413 on /api/base-photos/pretrip). */
const PDF_SCALE = 1.5
/** JPEG quality for page previews — much smaller than PNG; sufficient for flipbook display. */
const PAGE_JPEG_QUALITY = 0.88

/** Must be set before getDocument(); use CDN so no copy in public is required. */
function ensureWorkerSrc(): void {
  if (typeof window === 'undefined') return
  if (GlobalWorkerOptions.workerSrc) return
  // Match worker version to installed pdfjs-dist (build/pdf.worker.mjs)
  GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.10.38/build/pdf.worker.mjs'
}

export type PdfToImagesResult = {
  pageImageUrls: string[]
  pdfDownloadUrl: string
}

/**
 * Render each page to a JPEG data URL (smaller than PNG for upload) and expose the PDF as an object URL.
 * Run in browser only (uses canvas and createObjectURL).
 */
export async function pdfFileToImages(file: File): Promise<PdfToImagesResult> {
  if (typeof window === 'undefined') {
    throw new Error('pdfFileToImages must run in the browser')
  }
  ensureWorkerSrc()

  const arrayBuffer = await file.arrayBuffer()
  const pdf = await getDocument({ data: arrayBuffer }).promise
  const numPages = pdf.numPages
  const pageImageUrls: string[] = []

  for (let i = 1; i <= numPages; i++) {
    const page = await pdf.getPage(i)
    const viewport = page.getViewport({ scale: PDF_SCALE })
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not get canvas context')
    canvas.width = viewport.width
    canvas.height = viewport.height
    await page.render({
      canvasContext: ctx,
      viewport,
      intent: 'display',
    }).promise
    const dataUrl = canvas.toDataURL('image/jpeg', PAGE_JPEG_QUALITY)
    pageImageUrls.push(dataUrl)
  }

  const pdfDownloadUrl = URL.createObjectURL(file)
  return { pageImageUrls, pdfDownloadUrl }
}
